import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminNavigation from './AdminNavigation';
import './UserManagement.css';
import { API_URL } from '../utils/config';

const UserManagement = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const usersPerPage = 10;

  const [editFormData, setEditFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    isAdmin: false
  });

  useEffect(() => {
    const adminToken = localStorage.getItem('adminToken');
    if (!adminToken) {
      navigate('/admin/login');
      return;
    }
    fetchUsers();
  }, [navigate]);

  const fetchUsers = async () => {
    try {
      const adminToken = localStorage.getItem('adminToken');
      // Use /api/admin/users
      const response = await axios.get(`${API_URL}/api/admin/users`, {
        headers: {
          'Authorization': `Bearer ${adminToken}`
        }
      });
      setUsers(response.data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
      setLoading(false);
    } catch (error) {
      console.error('Error fetching users:', error);
      setMessage({ text: 'Error loading users', type: 'error' });
      setLoading(false);
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setEditFormData({
      name: user.name || '',
      email: user.email || '',
      phone: user.phone || '',
      address: user.address || '',
      isAdmin: user.isAdmin || false
    });
    setShowEditModal(true);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      const adminToken = localStorage.getItem('adminToken');
      // Use /api/admin/users
      await axios.put(
        `${API_URL}/api/admin/users/${editingUser._id}`,
        editFormData,
        {
          headers: {
            'Authorization': `Bearer ${adminToken}`
          }
        }
      );
      
      setMessage({ text: 'User updated successfully!', type: 'success' });
      setShowEditModal(false);
      fetchUsers();
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
    } catch (error) {
      console.error('Error updating user:', error);
      setMessage({ text: 'Error updating user', type: 'error' });
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user account?')) {
      try {
        const adminToken = localStorage.getItem('adminToken');
        // Use /api/admin/users
        await axios.delete(`${API_URL}/api/admin/users/${userId}`, {
          headers: {
            'Authorization': `Bearer ${adminToken}`
          }
        });
        
        setMessage({ text: 'User deleted successfully!', type: 'success' });
        fetchUsers();
        setTimeout(() => setMessage({ text: '', type: '' }), 3000);
      } catch (error) {
        console.error('Error deleting user:', error);
        setMessage({ text: 'Error deleting user', type: 'error' });
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditFormData({
      ...editFormData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  // Filter users
  const filteredUsers = users.filter(user => 
    user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.phone?.includes(searchTerm)
  );

  // Pagination
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const calculateUserStats = () => {
    return {
      total: users.length,
      admins: users.filter(u => u.isAdmin).length,
      customers: users.filter(u => !u.isAdmin).length,
      recent: users.filter(u => {
        const userDate = new Date(u.createdAt);
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);
        return userDate > weekAgo;
      }).length
    };
  };

  const stats = calculateUserStats();

  return (
    <>
      <AdminNavigation />
      <div className="user-management-container">
        <div className="user-header">
          <h1>👥 User Management</h1>
          <p>View and manage all registered users</p>
        </div>

        {message.text && (
          <div className={`message ${message.type}`}>
            {message.text}
          </div>
        )}

        {/* Statistics Cards */}
        <div className="user-stats-grid">
          <div className="user-stat-card">
            <div className="stat-icon">👥</div>
            <div className="stat-info">
              <h3>{stats.total}</h3>
              <p>Total Users</p>
            </div>
          </div>
          <div className="user-stat-card">
            <div className="stat-icon">🛡️</div>
            <div className="stat-info">
              <h3>{stats.admins}</h3>
              <p>Admins</p>
            </div>
          </div>
          <div className="user-stat-card">
            <div className="stat-icon">👤</div>
            <div className="stat-info">
              <h3>{stats.customers}</h3>
              <p>Customers</p>
            </div>
          </div>
          <div className="user-stat-card highlight">
            <div className="stat-icon">🆕</div>
            <div className="stat-info">
              <h3>{stats.recent}</h3>
              <p>New (7 days)</p>
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <div className="search-section">
          <div className="search-box">
            <span className="search-icon">🔍</span>
            <input
              type="text"
              placeholder="Search by name, email or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Users Table */}
        <div className="users-section">
          <h2>All Users ({filteredUsers.length})</h2>
          
          {loading ? (
            <div className="loading">Loading users...</div>
          ) : filteredUsers.length === 0 ? (
            <div className="no-users">
              <p>No users found</p>
            </div>
          ) : (
            <>
              <div className="users-table-container">
                <table className="users-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Address</th>
                      <th>Role</th>
                      <th>Joined</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentUsers.map((user, index) => (
                      <tr key={user._id}>
                        <td>{indexOfFirstUser + index + 1}</td>
                        <td>
                          <div className="user-name">
                            <span className="user-avatar">
                              {user.name?.charAt(0).toUpperCase() || '?'}
                            </span>
                            {user.name || 'N/A'}
                          </div>
                        </td>
                        <td>{user.email || 'N/A'}</td>
                        <td>{user.phone || 'N/A'}</td>
                        <td className="address-cell">
                          {user.address || 'Not provided'}
                        </td>
                        <td>
                          <span className={`role-badge ${user.isAdmin ? 'admin' : 'customer'}`}>
                            {user.isAdmin ? '🛡️ Admin' : '👤 Customer'}
                          </span>
                        </td>
                        <td>{formatDate(user.createdAt)}</td>
                        <td>
                          <div className="action-buttons">
                            <button
                              className="btn-edit-user"
                              onClick={() => handleEditUser(user)}
                            >
                              ✏️
                            </button>
                            <button
                              className="btn-delete-user"
                              onClick={() => handleDeleteUser(user._id)}
                            >
                              🗑️
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="pagination">
                  <button
                    className="pagination-btn"
                    onClick={() => paginate(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    ← Previous
                  </button>

                  <div className="pagination-numbers">
                    {[...Array(totalPages)].map((_, index) => (
                      <button
                        key={index + 1}
                        className={`pagination-number ${currentPage === index + 1 ? 'active' : ''}`}
                        onClick={() => paginate(index + 1)}
                      >
                        {index + 1}
                      </button>
                    ))}
                  </div>

                  <button
                    className="pagination-btn"
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next →
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Edit User Modal */}
        {showEditModal && (
          <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>✏️ Edit User</h3>
                <button 
                  className="modal-close"
                  onClick={() => setShowEditModal(false)}
                >
                  ✕
                </button>
              </div>
              
              <form onSubmit={handleUpdateUser} className="edit-form">
                <div className="form-group">
                  <label>Name</label>
                  <input
                    type="text"
                    name="name"
                    value={editFormData.name}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    name="email"
                    value={editFormData.email}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="text"
                    name="phone"
                    value={editFormData.phone}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="form-group">
                  <label>Address</label>
                  <textarea
                    name="address"
                    value={editFormData.address}
                    onChange={handleInputChange}
                    rows="3"
                  ></textarea>
                </div>

                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      name="isAdmin"
                      checked={editFormData.isAdmin}
                      onChange={handleInputChange}
                    />
                    <span>Make this user an admin</span>
                  </label>
                </div>

                <div className="modal-actions">
                  <button type="submit" className="btn-save">
                    💾 Save Changes
                  </button>
                  <button 
                    type="button" 
                    className="btn-cancel-modal"
                    onClick={() => setShowEditModal(false)}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default UserManagement;